import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import useAuth from "@/hooks/use-auth";
import apiClient from "@/api/apiClient";
import { Eye, EyeOff, Code } from "lucide-react";
import { saveCredentials, hasRememberedCredentials } from "@/lib/utils";

// Schema cho form đăng nhập
const loginSchema = z.object({
  username: z.string().min(1, { message: "Tên đăng nhập không được để trống" }),
  password: z.string().min(1, { message: "Mật khẩu không được để trống" }),
  rememberMe: z.boolean().default(false),
});

// Schema cho form quên mật khẩu
const forgotPasswordSchema = z.object({
  email: z.string().email({ message: "Email không hợp lệ" }),
});

type LoginFormValues = z.infer<typeof loginSchema>;
type ForgotPasswordFormValues = z.infer<typeof forgotPasswordSchema>;

// Định nghĩa kiểu dữ liệu response từ API
interface ApiResponse<T> {
  success: boolean;
  message: string;
  data: T;
}

interface LoginData {
  user: {
    id: number;
    username: string;
    email: string;
    full_name: string;
    phone: string;
    role: string;
    status: string;
    created_at: string;
    updated_at: string;
    avatar_url: string;
    email_verified: boolean;
    phone_verified: boolean;
    last_login: string;
  };
  token: string;
  refreshToken: string;
  expiresIn: string;
}

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, devLogin, isDevelopment } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<string>("login");
  const [showPassword, setShowPassword] = useState(false);
  
  // Lấy đường dẫn chuyển hướng từ state (nếu có)
  const from = location.state?.from || "/";

  // Form đăng nhập
  const loginForm = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      username: localStorage.getItem("rememberedUsername") || "",
      password: localStorage.getItem("rememberedPassword") || "",
      rememberMe: hasRememberedCredentials(),
    },
  });

  // Form quên mật khẩu
  const forgotPasswordForm = useForm<ForgotPasswordFormValues>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: {
      email: "",
    },
  });

  // Xử lý đăng nhập
  const onLoginSubmit = async (formData: LoginFormValues) => {
    setIsLoading(true);
    
    try {
      // Lưu thông tin đăng nhập nếu người dùng chọn "Ghi nhớ đăng nhập"
      saveCredentials(formData.username, formData.password, formData.rememberMe);
      
      // Chuyển formData thành chuỗi không null/undefined
      const credentials = {
        username: formData.username || "",
        password: formData.password || "",
      };
      
      const result = await login(credentials);
      
      if (result?.success) {
        // Chuyển hướng đến trang được yêu cầu trước đó hoặc trang chủ
        navigate(from, { replace: true });
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Xử lý quên mật khẩu
  const onForgotPasswordSubmit = async (data: ForgotPasswordFormValues) => {
    setIsLoading(true);
    
    try {
      const response = await apiClient.post<ApiResponse<any>>("/api/auth/forgot-password", data);

      if (response.success) {
        toast({
          title: "Yêu cầu đặt lại mật khẩu đã được gửi",
          description: response.message || "Vui lòng kiểm tra email của bạn để đặt lại mật khẩu.",
          variant: "default",
        });
        // Chuyển về tab đăng nhập
        setActiveTab("login");
      } else {
        toast({
          title: "Yêu cầu thất bại",
          description: response.message || "Không thể gửi yêu cầu đặt lại mật khẩu. Vui lòng thử lại sau.",
          variant: "destructive",
        });
      }
    } catch (error: any) {
      toast({
        title: "Yêu cầu thất bại",
        description: error.message || "Đã xảy ra lỗi khi gửi yêu cầu. Vui lòng thử lại sau.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Xử lý đăng nhập DEV mode
  const handleDevLogin = async () => {
    setIsLoading(true);
    
    try {
      const result = await devLogin();
      
      if (result?.success) {
        // Chuyển hướng đến trang được yêu cầu trước đó hoặc trang chủ
        navigate(from, { replace: true });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-background">
      {/* Banner - hiển thị bên trái trên desktop, trên cùng trên mobile */}
      <div 
        className="md:w-1/2 h-[30vh] md:h-screen bg-cover bg-center" 
        style={{ 
          backgroundImage: `url('/assets/images/banner.jpg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="h-full w-full flex items-center justify-center bg-black bg-opacity-40">
          <div className="text-center text-white p-4">
            <h1 className="text-3xl md:text-4xl font-bold mb-4">Hệ thống quản lý</h1>
            <p className="text-lg md:text-xl">Giao hàng bến xe</p>
          </div>
        </div>
      </div>

      {/* Phần đăng nhập - hiển thị bên phải trên desktop, bên dưới trên mobile */}
      <div className="md:w-1/2 flex flex-grow items-center justify-center p-6 bg-gradient-to-br from-primary/10 to-accent/10">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="text-center mb-6 flex flex-col items-center">
            <div className="w-full max-w-[240px]">
              <img 
                src="/assets/images/logo.png" 
                alt="Giao hàng bến xe" 
                className="w-full h-auto object-contain"
              />
            </div>
            <h2 className="text-2xl font-bold mt-4 bg-clip-text text-transparent bg-gradient-to-r from-primary to-accent">
              Đăng nhập hệ thống
            </h2>
          </div>

          <Card className="border-2 shadow-lg overflow-hidden">
            <Tabs 
              value={activeTab} 
              onValueChange={setActiveTab} 
              className="w-full"
            >
              <TabsList className="grid w-full grid-cols-2 bg-gradient-to-r from-primary/80 to-accent/80">
                <TabsTrigger value="login">Đăng nhập</TabsTrigger>
                <TabsTrigger value="forgot">Quên mật khẩu</TabsTrigger>
              </TabsList>
              
              <div className="relative w-full">
                <div className={`transition-all duration-300 ${activeTab === 'login' ? 'translate-x-0' : '-translate-x-full opacity-0 absolute pointer-events-none'}`}>
                  <Form {...loginForm}>
                    <form onSubmit={loginForm.handleSubmit(onLoginSubmit)}>
                      <CardHeader>
                        <CardTitle>Đăng nhập</CardTitle>
                        <CardDescription>
                          Nhập thông tin đăng nhập của bạn để truy cập vào hệ thống.
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <FormField
                          control={loginForm.control}
                          name="username"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Tên đăng nhập</FormLabel>
                              <FormControl>
                                <Input
                                  placeholder="Nhập tên đăng nhập"
                                  {...field}
                                  disabled={isLoading}
                                  className="border-primary/30 focus:border-primary"
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={loginForm.control}
                          name="password"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Mật khẩu</FormLabel>
                              <FormControl>
                                <div className="relative">
                                  <Input
                                    type={showPassword ? "text" : "password"}
                                    placeholder="Nhập mật khẩu"
                                    {...field}
                                    disabled={isLoading}
                                    className="border-primary/30 focus:border-primary pr-10"
                                  />
                                  <button
                                    type="button"
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-primary"
                                    onClick={togglePasswordVisibility}
                                  >
                                    {showPassword ? (
                                      <EyeOff size={18} />
                                    ) : (
                                      <Eye size={18} />
                                    )}
                                  </button>
                                </div>
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={loginForm.control}
                          name="rememberMe"
                          render={({ field }) => (
                            <FormItem className="flex flex-row items-center space-x-3 space-y-0 mt-4">
                              <FormControl>
                                <Checkbox
                                  checked={field.value}
                                  onCheckedChange={field.onChange}
                                  id="rememberMe"
                                  className="data-[state=checked]:bg-gradient-to-r data-[state=checked]:from-primary data-[state=checked]:to-accent"
                                />
                              </FormControl>
                              <div className="space-y-1 leading-none">
                                <FormLabel htmlFor="rememberMe" className="cursor-pointer">
                                  Ghi nhớ đăng nhập
                                </FormLabel>
                              </div>
                            </FormItem>
                          )}
                        />
                      </CardContent>
                      <CardFooter className="flex flex-col space-y-2">
                        <Button
                          type="submit"
                          className="w-full bg-gradient-to-r from-primary to-accent hover:from-primary/90 hover:to-accent/90"
                          disabled={isLoading}
                        >
                          {isLoading ? "Đang xử lý..." : "Đăng nhập"}
                        </Button>
                        
                        {/* Nút đăng nhập DEV chỉ hiển thị trong môi trường development */}
                        {isDevelopment && (
                          <Button
                            type="button"
                            variant="outline"
                            className="w-full border-amber-500 border-dashed border-2 hover:bg-amber-500/20 text-amber-600 mt-4 flex items-center justify-center"
                            onClick={handleDevLogin}
                            disabled={isLoading}
                          >
                            <Code className="mr-2 h-4 w-4" />
                            Truy cập DEV
                          </Button>
                        )}
                      </CardFooter>
                    </form>
                  </Form>
                </div>
                
                <div className={`transition-all duration-300 ${activeTab === 'forgot' ? 'translate-x-0' : 'translate-x-full opacity-0 absolute pointer-events-none'}`}>
                  <Form {...forgotPasswordForm}>
                    <form onSubmit={forgotPasswordForm.handleSubmit(onForgotPasswordSubmit)}>
                      <CardHeader>
                        <CardTitle>Quên mật khẩu</CardTitle>
                        <CardDescription>
                          Nhập địa chỉ email của bạn, chúng tôi sẽ gửi hướng dẫn đặt lại mật khẩu.
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <FormField
                          control={forgotPasswordForm.control}
                          name="email"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Email</FormLabel>
                              <FormControl>
                                <Input
                                  type="email"
                                  placeholder="name@example.com"
                                  {...field}
                                  disabled={isLoading}
                                  className="border-primary/30 focus:border-primary"
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </CardContent>
                      <CardFooter className="flex flex-col space-y-2 pb-6">
                        <Button
                          type="submit"
                          className="w-full bg-gradient-to-r from-primary to-accent hover:from-primary/90 hover:to-accent/90"
                          disabled={isLoading}
                        >
                          {isLoading ? "Đang xử lý..." : "Gửi yêu cầu"}
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          className="w-full border-primary/30 hover:border-primary"
                          onClick={() => setActiveTab("login")}
                          disabled={isLoading}
                        >
                          Quay lại đăng nhập
                        </Button>
                      </CardFooter>
                    </form>
                  </Form>
                </div>
              </div>
            </Tabs>
          </Card>

          <div className="text-center text-sm text-muted-foreground mt-4">
            <p>&copy; {new Date().getFullYear()} Giao hàng bến xe. All rights reserved.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login; 